import { useEffect, useState, useMemo } from "react";
import { Users, CreditCard, UserPlus, UserCheck, DollarSign, UserX } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { type AffiliateRecord, type AffiliateStats, type AffiliateTrends, type TrendPoint, getAffiliateStats, getAffiliateTrends } from "@/lib/affiliate";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  affiliate: AffiliateRecord;
}

type TimeWindow = "7d" | "30d" | "90d" | "1yr";

const TIME_WINDOWS: TimeWindow[] = ["7d", "30d", "90d", "1yr"];

const windowToBucketKey: Record<TimeWindow, "last7d" | "last30d" | "last90d" | "last1yr"> = {
  "7d": "last7d",
  "30d": "last30d",
  "90d": "last90d",
  "1yr": "last1yr",
};

const metrics = [
  { key: "signedUp" as const, label: "Signed Up", icon: UserPlus, color: "text-blue-500", chartColor: "#3b82f6" },
  { key: "onboarded" as const, label: "Onboarded", icon: UserCheck, color: "text-emerald-500", chartColor: "#10b981" },
  { key: "paying" as const, label: "Paying", icon: DollarSign, color: "text-violet-500", chartColor: "#8b5cf6" },
  { key: "churned" as const, label: "Churned", icon: UserX, color: "text-red-400", chartColor: "#f87171" },
];

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="py-5">
            <Skeleton className="h-4 w-20 mb-3" />
            <Skeleton className="h-8 w-12" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function formatDateLabel(date: string, granularity: "day" | "month"): string {
  if (granularity === "month") {
    const [y, m] = date.split("-");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
  }
  const d = new Date(date + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function computeCumulative(points: TrendPoint[]): TrendPoint[] {
  let signedUp = 0, onboarded = 0, paying = 0, churned = 0;
  return points.map((pt) => ({
    date: pt.date,
    signedUp: signedUp += pt.signedUp,
    onboarded: onboarded += pt.onboarded,
    paying: paying += pt.paying,
    churned: churned += pt.churned,
  }));
}

export function AffiliateDashboard({ affiliate }: Props) {
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [trends, setTrends] = useState<AffiliateTrends | null>(null);
  const [loading, setLoading] = useState(true);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeWindow, setTimeWindow] = useState<TimeWindow>("1yr");

  useEffect(() => {
    getAffiliateStats()
      .then(setStats)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load stats"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setTrendsLoading(true);
    getAffiliateTrends(timeWindow)
      .then(setTrends)
      .catch(() => {})
      .finally(() => setTrendsLoading(false));
  }, [timeWindow]);

  const chartData = useMemo(() => {
    if (!trends) return [];
    const cumulative = computeCumulative(trends.points);
    return cumulative.map((pt) => ({
      ...pt,
      label: formatDateLabel(pt.date, trends.granularity),
    }));
  }, [trends]);

  return (
    <Tabs defaultValue="users" className="w-full">
      <TabsList>
        <TabsTrigger value="users" className="gap-1.5">
          <Users className="size-4" />
          Users
        </TabsTrigger>
        <TabsTrigger value="payouts" className="gap-1.5">
          <CreditCard className="size-4" />
          Payouts
        </TabsTrigger>
      </TabsList>

      <TabsContent value="users" className="mt-4">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Referral performance</p>
            <ToggleGroup
              type="single"
              value={timeWindow}
              onValueChange={(v) => { if (v) setTimeWindow(v as TimeWindow); }}
              size="sm"
            >
              {TIME_WINDOWS.map((w) => (
                <ToggleGroupItem key={w} value={w} className="text-xs px-2.5">
                  {w === "1yr" ? "1YR" : w.toUpperCase()}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          {loading && <StatsSkeleton />}

          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
          )}

          {!loading && !error && stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {metrics.map((m) => {
                const count = stats[m.key][windowToBucketKey[timeWindow]];
                const Icon = m.icon;
                return (
                  <Card key={m.key}>
                    <CardContent className="py-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className={`size-4 ${m.color}`} />
                        <p className="text-sm font-medium text-muted-foreground">{m.label}</p>
                      </div>
                      <p className="text-3xl font-bold">{count}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Cumulative area chart */}
          {!trendsLoading && chartData.length > 0 && (
            <Card>
              <CardContent className="pt-5 pb-2 pr-2">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      {metrics.map((m) => (
                        <linearGradient key={m.key} id={`grad-${m.key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={m.chartColor} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={m.chartColor} stopOpacity={0.03} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      tickLine={false}
                      axisLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                      labelStyle={{ color: "hsl(var(--foreground))" }}
                    />
                    {metrics.map((m) => (
                      <Area
                        key={m.key}
                        type="monotone"
                        dataKey={m.key}
                        name={m.label}
                        stroke={m.chartColor}
                        fill={`url(#grad-${m.key})`}
                        strokeWidth={2}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {trendsLoading && (
            <Card>
              <CardContent className="py-5">
                <Skeleton className="h-[200px] w-full" />
              </CardContent>
            </Card>
          )}
        </div>
      </TabsContent>

      <TabsContent value="payouts" className="mt-4">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10">
            <CreditCard className="size-8 text-muted-foreground" />
            <div className="text-center">
              <h3 className="text-base font-semibold">Payouts</h3>
              <p className="text-sm text-muted-foreground mt-1">Coming soon...</p>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
